/**
 * Domains, and the DNS record that proves an organization controls one.
 *
 * This is the whole basis on which an SSO-minted stranger becomes a member of a tenant
 * (`app_private.join_org_by_verified_domain`, 20260911000200), so every function here is a
 * security boundary rather than a formatting helper. `security.md:418` lists "self-service org
 * creation probing others' domains" as threat E and names DNS verification as its mitigation.
 */

/**
 * The TXT record's NAME is a dedicated `_`-prefixed label, never the apex.
 *
 * Reading the apex would mean parsing a customer's SPF, DMARC and every other vendor's
 * verification string out of one answer, and apex TXT sets are frequently at their size limit
 * already. A label of our own means the answer we read contains only records somebody published
 * for us, and adding one cannot break their mail.
 *
 * `_`-prefixed because RFC 8552 reserves that space for exactly this — attribute leaves that are
 * not hostnames — so it can never collide with a real host.
 */
export const VERIFICATION_LABEL = '_flowza-verification'

/** The value's prefix. Present so a human reading their own zone can tell what the record is for. */
export const VERIFICATION_PREFIX = 'flowza-domain-verification='

/** Where the organization publishes the record for `domain`. */
export function verificationRecordName(domain: string): string {
  return `${VERIFICATION_LABEL}.${domain}`
}

/** What they publish there. */
export function verificationRecordValue(token: string): string {
  return `${VERIFICATION_PREFIX}${token}`
}

/**
 * A fresh verification token: 32 lower-case hex characters, 128 bits from the platform CSPRNG.
 *
 * Never derived from the org id, the domain or a timestamp. A token anybody can compute is a token
 * anybody can publish, and publishing it is the entire proof — so a guessable one would let a
 * stranger verify a domain they do not control, provided only that they can also get a record into
 * its zone, which is exactly the situation a shared or delegated subdomain creates.
 */
export function newVerificationToken(): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Normalize what a person typed into the domain this system will store, or null.
 *
 * Deliberately strict, and it REFUSES rather than repairs wherever repairing would change which
 * domain is meant. Accepting `contoso.com/evil` by silently truncating it is how somebody verifies
 * one thing and believes they verified another.
 *
 * Tolerated because they are unambiguous: surrounding whitespace, upper case, a `https://` scheme,
 * a trailing slash or path, a port, a single trailing dot (the DNS root), and a leading `@` or
 * `www.`-free host taken from a pasted address.
 *
 * Refused: anything non-ASCII (an IDN must be punycoded by the caller who knows the user's intent —
 * `ck_org_domains__domain` admits only a-z0-9-, and silently mangling a Unicode domain into
 * something that passes would be worse than refusing), a bare label with no dot, a leading or
 * trailing hyphen in any label, and anything over 253 characters.
 */
export function normalizeDomain(input: string): string | null {
  let value = input.trim().toLowerCase()
  if (value.length === 0) return null

  // A pasted email address: take the domain half.
  const at = value.lastIndexOf('@')
  if (at !== -1) value = value.slice(at + 1)

  // A pasted URL: drop the scheme, then anything from the first `/`, `?` or `#`.
  value = value.replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
  const path = value.search(/[/?#]/)
  if (path !== -1) value = value.slice(0, path)

  // A port.
  const colon = value.indexOf(':')
  if (colon !== -1) value = value.slice(0, colon)

  // The DNS root's trailing dot.
  if (value.endsWith('.')) value = value.slice(0, -1)

  if (value.length === 0 || value.length > 253) return null
  // ASCII only — see the doc comment on IDNs.
  if (!/^[a-z0-9.-]+$/.test(value)) return null

  const labels = value.split('.')
  if (labels.length < 2) return null
  for (const label of labels) {
    if (label.length === 0 || label.length > 63) return null
    if (label.startsWith('-') || label.endsWith('-')) return null
  }
  return value
}

/**
 * The domain half of an address, normalized, or null.
 *
 * Used on the sign-in path to ask which organization owns an address's domain BEFORE any session
 * exists. It must agree exactly with what `join_org_by_verified_domain` does in SQL
 * (`split_part(lower(btrim(email)), '@', 2)`), or the browser would route somebody to an IdP the
 * database will then refuse to make them a member of.
 */
export function domainOfEmail(email: string): string | null {
  const value = email.trim().toLowerCase()
  const at = value.lastIndexOf('@')
  if (at <= 0 || at === value.length - 1) return null
  return normalizeDomain(value.slice(at + 1))
}
